import type { NoteValidityStatus, StockNoteCategory, StockNoteItem, StockNotesTabProps } from "fin-web-components";
import { useCallback, useMemo, useState } from "react";
import { UserStockNoteApi } from "../../products/user_holdings/UserStockNoteApi";
import type { UserStockNoteDTO } from "../../products/user_holdings/UserStockNoteType";

type PanelCache = {
  loading: boolean;
  creating: boolean;
  editing: boolean;
  markingNoteId: string | null;
  markingStatus: NoteValidityStatus | null;
  notes: StockNoteItem[];
};

const EMPTY_PANEL: PanelCache = {
  loading: false,
  creating: false,
  editing: false,
  markingNoteId: null,
  markingStatus: null,
  notes: [],
};

function parseValidityStatus(raw: unknown): NoteValidityStatus | null {
  const s = String(raw ?? "").trim();
  if (s === "valid" || s === "invalid") {
    return s;
  }
  return null;
}

function toNoteItem(row: UserStockNoteDTO): StockNoteItem | null {
  const id = String(row.id ?? "").trim();
  const content = String(row.content ?? "").trim();
  const category = String(row.category ?? "").trim() as StockNoteCategory;
  if (!id || !content) {
    return null;
  }
  const validCategory: StockNoteCategory =
    category === "基本面" || category === "消息面" || category === "技术面"
      ? category
      : "基本面";
  return {
    id,
    eventTime: String(row.eventTime ?? ""),
    content,
    category: validCategory,
    validityStatus: parseValidityStatus(row.validityStatus),
    stockCode: String(row.stockCode ?? "").trim() || undefined,
    stockName: String(row.stockName ?? "").trim() || undefined,
  };
}

function applyNotes(data: { notes?: UserStockNoteDTO[] } | null | undefined): StockNoteItem[] {
  return (data?.notes ?? []).map(toNoteItem).filter((x): x is StockNoteItem => x != null);
}

function sortNotes(notes: StockNoteItem[]): StockNoteItem[] {
  return [...notes].sort((a, b) => b.eventTime.localeCompare(a.eventTime));
}

function mergeCreatedNote(notes: StockNoteItem[], created: StockNoteItem): StockNoteItem[] {
  return sortNotes([created, ...notes.filter((n) => n.id !== created.id)]);
}

function mergeUpdatedNote(notes: StockNoteItem[], updated: StockNoteItem): StockNoteItem[] {
  return sortNotes(notes.map((n) => (n.id === updated.id ? updated : n)));
}

export function useStockNotesTab(spaceId: string) {
  const [panels, setPanels] = useState<Record<string, PanelCache>>({});

  const loadPanel = useCallback(
    async (stockCode: string) => {
      const code = stockCode.trim().toUpperCase();
      if (!code || !spaceId) {
        return;
      }
      setPanels((prev) => ({
        ...prev,
        [code]: { ...(prev[code] ?? EMPTY_PANEL), loading: true },
      }));
      const data = await UserStockNoteApi.queryPanel(spaceId, code);
      setPanels((prev) => ({
        ...prev,
        [code]: {
          loading: false,
          creating: false,
          editing: false,
          markingNoteId: null,
          markingStatus: null,
          notes: applyNotes(data),
        },
      }));
    },
    [spaceId]
  );

  const createNote = useCallback(
    async (
      stockCode: string,
      stockName: string,
      draft: { content: string; category: StockNoteCategory }
    ) => {
      const code = stockCode.trim().toUpperCase();
      if (!code || !spaceId) {
        return;
      }
      setPanels((prev) => ({
        ...prev,
        [code]: { ...(prev[code] ?? EMPTY_PANEL), creating: true },
      }));
      const data = await UserStockNoteApi.create(spaceId, {
        stockCode: code,
        stockName,
        content: draft.content,
        category: draft.category,
      });
      const created = data ? toNoteItem(data) : null;
      setPanels((prev) => {
        const panel = prev[code] ?? EMPTY_PANEL;
        return {
          ...prev,
          [code]: {
            loading: false,
            creating: false,
            editing: false,
            markingNoteId: null,
            markingStatus: null,
            notes: created ? mergeCreatedNote(panel.notes, created) : panel.notes,
          },
        };
      });
    },
    [spaceId]
  );

  const editNote = useCallback(
    async (
      stockCode: string,
      noteId: string,
      draft: { content: string; category: StockNoteCategory }
    ) => {
      const code = stockCode.trim().toUpperCase();
      if (!code || !spaceId) {
        return;
      }
      setPanels((prev) => ({
        ...prev,
        [code]: { ...(prev[code] ?? EMPTY_PANEL), editing: true },
      }));
      const data = await UserStockNoteApi.update(spaceId, {
        noteId,
        content: draft.content,
        category: draft.category,
      });
      const updated = data ? toNoteItem(data) : null;
      setPanels((prev) => {
        const panel = prev[code] ?? EMPTY_PANEL;
        return {
          ...prev,
          [code]: {
            ...panel,
            creating: false,
            editing: false,
            markingNoteId: null,
            markingStatus: null,
            notes: updated ? mergeUpdatedNote(panel.notes, updated) : panel.notes,
          },
        };
      });
    },
    [spaceId]
  );

  const deleteNote = useCallback(
    async (stockCode: string, noteId: string) => {
      const code = stockCode.trim().toUpperCase();
      if (!code || !spaceId) {
        return;
      }
      const ok = await UserStockNoteApi.delete(spaceId, noteId);
      if (!ok) {
        return;
      }
      setPanels((prev) => ({
        ...prev,
        [code]: {
          ...(prev[code] ?? EMPTY_PANEL),
          notes: (prev[code]?.notes ?? []).filter((n) => n.id !== noteId),
        },
      }));
    },
    [spaceId]
  );

  const markNoteValidity = useCallback(
    async (stockCode: string, noteId: string, validityStatus: NoteValidityStatus): Promise<void> => {
      const code = stockCode.trim().toUpperCase();
      if (!code || !spaceId) {
        return;
      }
      setPanels((prev) => ({
        ...prev,
        [code]: {
          ...(prev[code] ?? EMPTY_PANEL),
          markingNoteId: noteId,
          markingStatus: validityStatus,
        },
      }));
      const data = await UserStockNoteApi.markValidity(spaceId, { noteId, validityStatus });
      const updated = data ? toNoteItem(data) : null;
      setPanels((prev) => {
        const panel = prev[code] ?? EMPTY_PANEL;
        return {
          ...prev,
          [code]: {
            ...panel,
            markingNoteId: null,
            markingStatus: null,
            notes: updated ? mergeUpdatedNote(panel.notes, updated) : panel.notes,
          },
        };
      });
    },
    [spaceId]
  );

  const buildStockNotesTabProps = useCallback(
    (stockCode: string, stockName?: string): StockNotesTabProps => {
      const code = stockCode.trim().toUpperCase();
      const panel = panels[code] ?? EMPTY_PANEL;
      return {
        loading: panel.loading,
        notes: panel.notes,
        creating: panel.creating,
        editing: panel.editing,
        markingNoteId: panel.markingNoteId,
        markingStatus: panel.markingStatus,
        onCreateNote: (draft) => createNote(code, stockName ?? "", draft),
        onEditNote: (noteId, draft) => editNote(code, noteId, draft),
        onDeleteNote: (noteId) => deleteNote(code, noteId),
        onMarkNoteValid: (noteId) => markNoteValidity(code, noteId, "valid"),
        onMarkNoteInvalid: (noteId) => markNoteValidity(code, noteId, "invalid"),
      };
    },
    [panels, createNote, editNote, deleteNote, markNoteValidity]
  );

  const listNotesForImport = useCallback(
    (stockCode: string): StockNoteItem[] => {
      const code = stockCode.trim().toUpperCase();
      return panels[code]?.notes ?? [];
    },
    [panels]
  );

  return useMemo(
    () => ({
      loadPanel,
      buildStockNotesTabProps,
      listNotesForImport,
      markNoteValidity,
    }),
    [loadPanel, buildStockNotesTabProps, listNotesForImport, markNoteValidity]
  );
}
